import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";
import { getToken, getUserId } from "../services/authorize";
import ThaiDateInput from '../components/ThaiDateInput'
import { toISODate } from '../utils/thaiDate'

const reservationLabel = {
  queued: "รอคิว",
  ready: "พร้อมรับ",
  fulfilled: "รับแล้ว",
  cancelled: "ยกเลิก",
  expired: "หมดอายุ"
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return toISODate(d);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const getDefaultExpireDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return toDateInputValue(d);
};

export default function LibraryReservations() {
  const [titles, setTitles] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    title_id: "",
    borrower_type: "student",
    borrower_idnumber: "",
    expire_date: getDefaultExpireDate(),
    reservation_note: ""
  });

  const headers = useMemo(() => ({ authorization: `Bearer ${getToken()}` }), []);

  const activeTitles = useMemo(
    () => titles.filter((title) => title.title_status === "active"),
    [titles]
  );

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const [titlesRes, reservationsRes] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getTitles`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getReservations`, { headers })
      ]);

      setTitles(titlesRes.data || []);
      setReservations(reservationsRes.data || []);
    } catch (fetchError) {
      setError("ไม่สามารถโหลดข้อมูลการจองได้");
      console.error("LibraryReservations fetch error:", fetchError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateReservation = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const t_id = getUserId();
    if (!t_id) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!form.title_id || !form.borrower_idnumber.trim()) {
      setError("กรุณาเลือกชื่อหนังสือและกรอกข้อมูลผู้จอง");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/reserveTitle`,
        {
          title_id: Number(form.title_id),
          borrower_type: form.borrower_type,
          borrower_idnumber: form.borrower_idnumber.trim(),
          expire_date: form.expire_date || null,
          reservation_note: form.reservation_note.trim() || null,
          t_id
        },
        { headers }
      );

      setSuccess(`สร้างรายการจองสำเร็จ เลขที่: ${response.data?.resv_no || "-"}`);
      setForm({
        title_id: "",
        borrower_type: "student",
        borrower_idnumber: "",
        expire_date: getDefaultExpireDate(),
        reservation_note: ""
      });
      await fetchData();
    } catch (submitError) {
      const message = submitError?.response?.data?.message;
      if (message === "student not found") {
        setError("ไม่พบเลขประจำตัวนักเรียน");
      } else if (message === "teacher not found") {
        setError("ไม่พบรหัสประจำตัวครูอาจารย์");
      } else if (message === "invalid borrower type") {
        setError("ประเภทผู้จองไม่ถูกต้อง");
      } else if (message === "title not found") {
        setError("ไม่พบชื่อหนังสือที่เลือก");
      } else if (message === "title inactive") {
        setError("ชื่อหนังสือนี้ถูกปิดใช้งาน");
      } else {
        setError("สร้างรายการจองไม่สำเร็จ");
      }
      console.error("Create reservation error:", submitError);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (resv_id, reservation_status) => {
    setError("");
    setSuccess("");

    const t_id = getUserId();
    if (!t_id) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    try {
      setUpdatingId(resv_id);
      await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/updateReservationStatus`,
        {
          resv_id,
          reservation_status,
          t_id
        },
        { headers }
      );

      setSuccess(`อัปเดตสถานะรายการจองเป็น ${reservationLabel[reservation_status] || reservation_status} สำเร็จ`);
      await fetchData();
    } catch (updateError) {
      const message = updateError?.response?.data?.message;
      if (message === "no available copy for reservation") {
        setError("ยังไม่มีเล่มพร้อมสำหรับอัปเดตสถานะนี้");
      } else if (message === "reservation already closed") {
        setError("รายการจองนี้ปิดรายการแล้ว");
      } else {
        setError("อัปเดตสถานะการจองไม่สำเร็จ");
      }
      console.error("Update reservation status error:", updateError);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">งานจองหนังสือ</h4>
          <Badge bg="info">Reservation</Badge>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-3">
            {success}
          </Alert>
        )}

        <Row className="g-3 mb-3">
          <Col lg={5}>
            <Card>
              <Card.Header className="fw-semibold">สร้างรายการจอง</Card.Header>
              <Card.Body>
                <Form onSubmit={handleCreateReservation}>
                  <Form.Group className="mb-3">
                    <Form.Label>หนังสือ</Form.Label>
                    <Form.Select
                      value={form.title_id}
                      onChange={(event) => setForm((prev) => ({ ...prev, title_id: event.target.value }))}
                      required
                    >
                      <option value="">-- เลือกหนังสือ --</option>
                      {activeTitles.map((title) => (
                        <option key={title.title_id} value={title.title_id}>
                          {title.title_code} - {title.title_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="d-block">ผู้จอง</Form.Label>
                    <div className="d-flex gap-3 mb-2">
                      <Form.Check
                        inline
                        type="radio"
                        id="reservation-borrower-student"
                        label="นักเรียน"
                        name="reservation_borrower_type"
                        checked={form.borrower_type === "student"}
                        onChange={() => setForm((prev) => ({ ...prev, borrower_type: "student" }))}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        id="reservation-borrower-teacher"
                        label="ครูอาจารย์"
                        name="reservation_borrower_type"
                        checked={form.borrower_type === "teacher"}
                        onChange={() => setForm((prev) => ({ ...prev, borrower_type: "teacher" }))}
                      />
                    </div>
                    <Form.Control
                      value={form.borrower_idnumber}
                      onChange={(event) => setForm((prev) => ({ ...prev, borrower_idnumber: event.target.value }))}
                      placeholder={form.borrower_type === "teacher" ? "เช่น T000123" : "เช่น 66123456"}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>วันหมดอายุการจอง</Form.Label>
                    <ThaiDateInput
                      value={form.expire_date}
                      onChange={(event) => setForm((prev) => ({ ...prev, expire_date: event.target.value }))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>หมายเหตุ</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={form.reservation_note}
                      onChange={(event) => setForm((prev) => ({ ...prev, reservation_note: event.target.value }))}
                      placeholder="รายละเอียดเพิ่มเติม"
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button type="submit" disabled={submitting || loading}>
                      {submitting ? "กำลังบันทึก..." : "สร้างรายการจอง"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={7}>
            <Card>
              <Card.Header className="fw-semibold">รายการจองล่าสุด</Card.Header>
              <Card.Body className="table-responsive">
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <Table hover size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Resv No</th>
                        <th>หนังสือ</th>
                        <th>ผู้จอง</th>
                        <th>คิว</th>
                        <th>สถานะ</th>
                        <th className="text-end">จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservations.slice(0, 100).map((row) => {
                        const closed = ["fulfilled", "cancelled", "expired"].includes(row.reservation_status);
                        return (
                          <tr key={row.resv_id}>
                            <td>{row.resv_no}</td>
                            <td>
                              <div>{row.title_name}</div>
                              <small className="text-muted">{row.copy_code || "ยังไม่จัดเล่ม"}</small>
                            </td>
                            <td>
                              <div>{row.borrower_name}</div>
                              <small className="text-muted">{row.borrower_idnumber}</small>
                            </td>
                            <td>{row.queue_no || "-"}</td>
                            <td>{reservationLabel[row.reservation_status] || row.reservation_status}</td>
                            <td className="text-end">
                              <div className="d-flex justify-content-end gap-1 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  disabled={closed || updatingId === row.resv_id}
                                  onClick={() => handleUpdateStatus(row.resv_id, "ready")}
                                >
                                  พร้อมรับ
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  disabled={closed || updatingId === row.resv_id}
                                  onClick={() => handleUpdateStatus(row.resv_id, "fulfilled")}
                                >
                                  รับแล้ว
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  disabled={closed || updatingId === row.resv_id}
                                  onClick={() => handleUpdateStatus(row.resv_id, "expired")}
                                >
                                  หมดอายุ
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  disabled={closed || updatingId === row.resv_id}
                                  onClick={() => handleUpdateStatus(row.resv_id, "cancelled")}
                                >
                                  ยกเลิก
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card>
          <Card.Header className="fw-semibold">รายละเอียดการจอง</Card.Header>
          <Card.Body className="table-responsive">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <Table hover size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>วันที่จอง</th>
                    <th>Resv No</th>
                    <th>ผู้จอง</th>
                    <th>หนังสือ</th>
                    <th>วันหมดอายุ</th>
                    <th>ผู้ดำเนินการ</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.slice(0, 100).map((row) => (
                    <tr key={`detail-${row.resv_id}`}>
                      <td>{formatDateTime(row.reserved_date)}</td>
                      <td>{row.resv_no}</td>
                      <td>{row.borrower_name}</td>
                      <td>{row.title_name}</td>
                      <td>{toDateInputValue(row.expire_date) || "-"}</td>
                      <td>{row.officer_name || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </main>
      <Footer />
    </React.Fragment>
  );
}
